import { Autocomplete, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { Job, Deal } from "../../../../api";
import { ColumnContext } from "./ColumnHelpers";
import { columnDisplayNames } from "../../../admin/ImportSettings";
import { getValidationErrorKey, isFieldRequired } from "../../../../utilities/jobValidation";
import { getDealLabel } from "../../../../utilities/utils";

export function column_Deal(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	return {
		id: "deal",
		header: columnDisplayNames.deal,
		accessorKey: "deal",
		enableEditing: false,
		size: 400,
		Cell: ({ row }: { row: MRT_Row<Job> }) => {
			const rowContext = context.getRowValidationContext(row);
			return (
				<Autocomplete
					fullWidth={true}
					size="small"
					value={
						context.props.allDeals.find(
							(deal) => deal.id === context.jobs.find((job) => job.id === row.original.id)?.dealIds[0],
						) || {
							id: "custom",
						}
					}
					options={[...context.props.allDeals, { id: "custom" }]}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					getOptionKey={(option) => option.id}
					renderInput={(params) => (
						<TextField
							error={!!context.validationErrors?.[getValidationErrorKey("deal", row.original.id)]}
							helperText={context.validationErrors?.[getValidationErrorKey("deal", row.original.id)]}
							required={isFieldRequired("deal", rowContext)}
							{...params}
						/>
					)}
					onChange={(event, newDeal) => {
						const newJobs = [...context.jobs];
						const newJob = newJobs.find((job) => {
							return job.id === row.original.id;
						});
						if (!newJob) return;
						const index = newJobs.indexOf(newJob);
						newJob.dealIds = [newDeal?.id || "custom"];
						row._valuesCache.dealIds = newJob.dealIds;
						if (newJob.dealIds.at(0) !== "custom") {
							const d = newDeal as Deal;
							newJob.importDensity = d.density;
							row._valuesCache.importDensity = newJob.importDensity;
							newJob.importDepth = d.depth;
							row._valuesCache.importDepth = newJob.importDepth;
							const testPackage = d.testPackage || "No test package specified";
							newJob.externalTestPackageId = Array.isArray(testPackage) ? testPackage.join("") : testPackage;
							row._valuesCache.importTestPackage = newJob.externalTestPackageId;
							newJob.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
							row._valuesCache.externalFrequencyOfMicros = newJob.externalFrequencyOfMicros;
						}
						newJobs[index] = newJob;
						context.setJobs(newJobs);
						context.validateJobs([newJob], { updateState: true, clearPrevious: false });
					}}
					getOptionLabel={(deal) => {
						if (deal && deal.id === "custom") {
							return "No linked Configuration";
						} else {
							return getDealLabel(deal as Deal);
						}
					}}
				/>
			);
		},
		AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => {
			return (
				<>
					<a style={{ fontWeight: "bold", fontSize: "1.2em" }}>Update Group: &nbsp;</a>
					<Autocomplete
						fullWidth={true}
						value={undefined}
						options={[...context.props.allDeals, { id: "custom" }]}
						getOptionKey={(option) => option.id}
						renderInput={(params) => <TextField {...params} />}
						onChange={(event, newDeal) => {
							const dealId = newDeal?.id || "custom";
							const d = newDeal as Deal;

							const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
								job.dealIds = [dealId];
								if (dealId !== "custom" && d) {
									job.importDensity = d.density;
									job.importDepth = d.depth;
									job.externalTestPackageId = d.testPackage || "No test package specified";
									job.externalFrequencyOfMicros = d.frequencyOfMicros ?? 0;
								}
							});

							context.setJobs(newJobs);
							context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
						}}
						getOptionLabel={(option) => {
							if (option && option.id === "custom") {
								return "Unlink All Configurations for Group";
							} else {
								const d = option as Deal;
								const dealLabelFields = [d.density, d.type, d.depth, d.testPackage || "No test package specified"];
								if (d.specialTag) dealLabelFields.push(d.specialTag);
								dealLabelFields.push(d.season);
								return `${d.density} Acre ${dealLabelFields.join(" - ")}`;
							}
						}}
					/>
				</>
			);
		},
	};
}
